import React, { useState, useMemo, useEffect } from 'react';
import {
  Popover,
  Paper,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Stack,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useSitesSummary } from '../../sites/hooks/useSitesSummary';
import { What3WordsLink } from '../../../shared/components/atoms/What3WordsLink';

interface AddSitesPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onAddSites: (siteIds: number[]) => void;
  userName: string;
  assignedSiteIds: number[];
  isSubmitting?: boolean;
}

export const AddSitesPopover: React.FC<AddSitesPopoverProps> = ({
  anchorEl,
  onClose,
  onAddSites,
  userName,
  assignedSiteIds,
  isSubmitting = false,
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedSiteIds, setSelectedSiteIds] = useState<Set<number>>(
    new Set()
  );

  const open = Boolean(anchorEl);
  const { data: sites = [], isLoading, error } = useSitesSummary();

  // Reset state when popover closes
  useEffect(() => {
    if (!open) {
      setSearchText('');
      setSelectedSiteIds(new Set());
    }
  }, [open]);

  // Filter out already assigned sites and apply search filter
  const availableSites = useMemo(() => {
    return sites
      .filter(site => !assignedSiteIds.includes(site.id))
      .filter(site =>
        site.name.toLowerCase().includes(searchText.toLowerCase())
      );
  }, [sites, assignedSiteIds, searchText]);

  const handleToggleSite = (siteId: number) => {
    setSelectedSiteIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(siteId)) {
        newSet.delete(siteId);
      } else {
        newSet.add(siteId);
      }
      return newSet;
    });
  };

  const handleAddSites = () => {
    if (selectedSiteIds.size > 0) {
      onAddSites(Array.from(selectedSiteIds));
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            width: 400,
            maxHeight: '70vh',
          },
        },
      }}
      data-testid="add-sites-popover"
    >
      <Paper>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            Add Sites to {userName}
          </Typography>

          <TextField
            fullWidth
            placeholder="Search sites..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              },
            }}
            sx={{ mt: 1 }}
            data-testid="search-sites-input"
          />
        </Box>

        <Divider />

        <Box
          sx={{
            maxHeight: 'calc(70vh - 180px)',
            overflowY: 'auto',
            px: 2,
            py: 1.5,
          }}
        >
          {isLoading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={32} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              Failed to load sites. Please try again.
            </Alert>
          )}

          {!isLoading && !error && availableSites.length === 0 && (
            <Box textAlign="center" py={3}>
              <Typography variant="body2" color="text.secondary">
                {searchText
                  ? 'No sites found matching your search.'
                  : 'All sites are already assigned to this user.'}
              </Typography>
            </Box>
          )}

          {!isLoading && !error && availableSites.length > 0 && (
            <FormGroup>
              {availableSites.map(site => (
                <FormControlLabel
                  key={site.id}
                  control={
                    <Checkbox
                      checked={selectedSiteIds.has(site.id)}
                      onChange={() => handleToggleSite(site.id)}
                      disabled={isSubmitting}
                      data-testid={`site-checkbox-${site.id}`}
                      size="small"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">{site.name}</Typography>
                      {site.what3WordsAddress && (
                        <What3WordsLink
                          address={site.what3WordsAddress}
                          variant="caption"
                        />
                      )}
                    </Box>
                  }
                  sx={{
                    py: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    mx: 0,
                    '&:last-child': {
                      borderBottom: 'none',
                    },
                  }}
                />
              ))}
            </FormGroup>
          )}
        </Box>

        <Divider />

        <Box sx={{ p: 2, pt: 1.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              {selectedSiteIds.size === 0
                ? 'Select sites to add'
                : selectedSiteIds.size === 1
                  ? '1 site selected'
                  : `${selectedSiteIds.size} sites selected`}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={handleClose}
                disabled={isSubmitting}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
              <Button
                size="small"
                onClick={handleAddSites}
                variant="contained"
                disabled={selectedSiteIds.size === 0 || isSubmitting}
                data-testid="add-sites-button"
              >
                {isSubmitting ? 'Adding...' : 'Add'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Popover>
  );
};
